<script setup>
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '@dashhudson/dashing-ui/confirmation-dialog';
import { Toast, useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useLocalStorage } from '@/composables/useLocalStorage';
import { isDatadogSyntheticsBrowser } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { useImpersonatorStore } from '@/stores/impersonator';
import { useSettingsStore } from '@/stores/settings';
import { useTrackingStore } from '@/stores/tracking';
import { logger } from '@/utils/logger';
import { guessTimezone } from '@/utils/timezone';
import { xssStrip } from '@/utils/xss';

const router = useRouter();
const authStore = useAuthStore();
const settingsStore = useSettingsStore();
const impersonatorStore = useImpersonatorStore();
const trackingStore = useTrackingStore();

const alreadyCheckedTimezone = useLocalStorage('alreadyCheckedTimezone', {});

const route = useRoute();
const isReport = computed(() => !!route.meta?.report);

function getCheckTimezoneStatus() {
  const userId = authStore.identity?.id || 'anonymous';
  return !!alreadyCheckedTimezone.value[userId];
}

function setCheckTimezoneStatus(status) {
  const userId = authStore.identity?.id || 'anonymous';
  alreadyCheckedTimezone.value = {
    ...alreadyCheckedTimezone.value,
    [userId]: status,
  };
}

const identityLoaded = computed(() => !!authStore.identity?.id);
const noIdentityTimezone = computed(() => !authStore.identity?.time_zone_name);
const notImpersonating = computed(() => !impersonatorStore.isImpersonating);
const guessedTimezone = computed(() => guessTimezone());
const timezoneIsDifferent = computed(
  () => authStore.identity?.time_zone_name !== guessedTimezone.value,
);

const currentTimezoneLabel = computed(() => xssStrip(authStore.identity?.time_zone_name));
const guessedTimezoneLabel = computed(() => xssStrip(guessedTimezone.value));

const isMounted = ref(false);
const showedDialog = ref(false);

onMounted(() => {
  isMounted.value = true;
});

const DEFAULT_USER_TIMEZONE_GROUP = 'defaultTimezoneToastGroup';

const { addToast: showUpdateDefaultTimezoneToast, removeToastGroup } = useToast({
  group: DEFAULT_USER_TIMEZONE_GROUP,
  severity: 'info',
  message: `Your timezone was set to ${guessedTimezone.value} based on your current location.
          If this is incorrect, please update your timezone in Profile Settings.`,
  CTA: 'Go to Profile Settings',
});

function onDefaultTimezoneToastAction() {
  router.push('/settings/profile');
  removeToastGroup(DEFAULT_USER_TIMEZONE_GROUP);
}

const { addToast: showUpdateTimezoneToast } = useToast({
  severity: 'success',
  message: `Your timezone has been updated to ${guessedTimezone.value}`,
  life: 10000,
});

const { addToast: showUpdateTimezoneToastError } = useToast({
  severity: 'error',
  message: `Failed to update timezone to ${guessedTimezone.value}`,
  life: 10000,
});

function onUpdateTimezoneError(error, showToast = true) {
  let errorMessage = 'Unknown Error';
  if (error?.response?.data?.description) {
    errorMessage = error.response.data.description;
  }
  logger.error(`Unable to update user timezone ${errorMessage}`);

  trackingStore.track('Check Timezone', {
    action: 'error',
    timezone: guessedTimezone.value,
    error: errorMessage,
  });

  if (showToast) {
    showUpdateTimezoneToastError();
  }
}

const { showConfirmationDialog } = useConfirmationDialog({
  group: 'timezoneCheckDialog',
  header: `Update Your Timezone to ${guessedTimezoneLabel.value}`,
  acceptLabel: 'Update Timezone',
  rejectLabel: 'Keep Current Timezone',
  acceptSeverity: 'primary',
  onHide: () => {
    setCheckTimezoneStatus(true);
    trackingStore.track('Check Timezone', {
      action: 'closed',
      timezone: guessedTimezone.value,
    });
  },
  onAccept: async () => {
    try {
      await settingsStore.updateUser({
        userId: authStore.identity.id,
        data: { timeZoneName: guessedTimezone.value },
      });
      await authStore.updateIdentity({ data: { timeZoneName: guessedTimezone.value } });
      trackingStore.track('Check Timezone', {
        action: 'accepted',
        timezone: guessedTimezone.value,
      });
      showUpdateTimezoneToast();
    } catch (error) {
      onUpdateTimezoneError(error);
    }
  },
  onReject: () => {
    trackingStore.track('Check Timezone', {
      action: 'rejected',
      timezone: guessedTimezone.value,
    });
    setCheckTimezoneStatus(true);
  },
});

async function setDefaultUserTimezone() {
  const data = { timeZoneName: guessedTimezone.value };
  try {
    await settingsStore.updateUser({
      userId: authStore.identity.id,
      data,
    });
    trackingStore.track('Check Timezone', {
      action: 'default',
      timezone: guessedTimezone.value,
    });
  } catch (error) {
    onUpdateTimezoneError(error, false);
    return;
  }
  await authStore.updateIdentity({ data });
  if (authStore.identity.id) {
    showUpdateDefaultTimezoneToast();
  }
}

function showCheckDifferentTimezoneDialog() {
  trackingStore.track('Check Timezone', {
    action: 'shown',
    timezone: guessedTimezone.value,
  });
  showConfirmationDialog();
  showedDialog.value = true;
}

watch(
  () => [isMounted.value, identityLoaded.value, notImpersonating.value, guessedTimezone.value],
  () => {
    if (isDatadogSyntheticsBrowser || isReport.value || window.Cypress) {
      return;
    }

    if (
      isMounted.value &&
      identityLoaded.value &&
      notImpersonating.value &&
      guessedTimezone.value
    ) {
      if (noIdentityTimezone.value) {
        setDefaultUserTimezone();
      } else if (timezoneIsDifferent.value && !getCheckTimezoneStatus()) {
        showCheckDifferentTimezoneDialog();
      } else if (!timezoneIsDifferent.value && getCheckTimezoneStatus()) {
        setCheckTimezoneStatus(false);
      }
    }
  },
);
</script>

<template>
  <ConfirmationDialog group="timezoneCheckDialog">
    <template #message>
      Your time zone setting is
      <span class="dash-typo-base-semibold"> {{ currentTimezoneLabel }}</span
      >.<br /><br />
      Your computer is set to <span class="dash-typo-base-semibold">{{ guessedTimezoneLabel }}</span
      >.<br /><br />
      All posts and messages will show dates in this timezone, and any new scheduled content will
      also use it.<br /><br />
      Do you want to update your time zone to match your computer? You can change this anytime in
      Settings.
    </template>
  </ConfirmationDialog>
  <Toast :group="DEFAULT_USER_TIMEZONE_GROUP" @action="onDefaultTimezoneToastAction" />
</template>
